import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Invoice, InvoiceDetail } from '../api/types'
import {
  DueChip,
  Empty,
  ErrorBanner,
  Kpi,
  Loading,
  Panel,
  StatusChip,
  dateOnly,
  inr,
  num,
} from '../components/ui'

type Filter = 'all' | 'outstanding' | 'settled'

function InvoiceDrawer({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['portal-invoice', invoiceId],
    queryFn: () => api.get<InvoiceDetail>(`/portal/invoices/${invoiceId}`).then((r) => r.data),
  })

  const invoice = detailQuery.data

  // Intra-state supply splits into CGST + SGST; inter-state is a single IGST
  // line. Showing all three when only one applies is how a reader loses trust
  // in the arithmetic.
  const interState = invoice !== undefined && Number(invoice.igst) > 0

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="row">
            <div>
              <div className="page-title mono">{invoice?.invoice_number ?? 'Invoice'}</div>
              <div className="page-sub">
                {invoice ? `Raised ${dateOnly(invoice.invoice_date)}` : ''}
              </div>
            </div>
            <span className="spacer" />
            <button className="btn btn-sm" onClick={() => window.print()}>
              Print
            </button>
            <button className="btn btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="drawer-body">
          {detailQuery.isLoading ? (
            <Loading rows={6} />
          ) : detailQuery.error ? (
            <ErrorBanner error={detailQuery.error} />
          ) : invoice ? (
            <div className="stack">
              <div className="kpi-grid">
                <Kpi value={inr(invoice.total)} label="Invoice total" />
                <Kpi
                  value={inr(invoice.balance)}
                  label="Still owing"
                  tone={Number(invoice.balance) > 0 ? 'warn' : 'good'}
                  foot={Number(invoice.balance) > 0 ? `due ${dateOnly(invoice.due_date)}` : 'settled'}
                />
              </div>

              <Panel title="Invoice">
                <dl className="kv">
                  <dt>Status</dt>
                  <dd>
                    <StatusChip status={invoice.status} />
                  </dd>
                  <dt>Invoice date</dt>
                  <dd>{dateOnly(invoice.invoice_date)}</dd>
                  <dt>Payment due</dt>
                  <dd>{dateOnly(invoice.due_date)}</dd>
                  <dt>Your GSTIN</dt>
                  <dd className="mono">{invoice.customer_gstin ?? '—'}</dd>
                  <dt>Place of supply</dt>
                  <dd>{invoice.place_of_supply ?? '—'}</dd>
                </dl>
              </Panel>

              <Panel title={`Charges (${invoice.lines.length})`} bodyless>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Consignment</th>
                      <th className="num">Quantity</th>
                      <th className="num">Rate</th>
                      <th className="num">Amount</th>
                      <th className="num">GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((line) => (
                      <tr key={line.id}>
                        <td data-label="Description">
                          {line.description}
                          {line.sac_code && (
                            <div className="page-sub mono">SAC {line.sac_code}</div>
                          )}
                        </td>
                        <td data-label="Consignment" className="mono">
                          {line.shipment_number ?? '—'}
                        </td>
                        <td data-label="Quantity" className="num">
                          {num(line.quantity, 2)}
                        </td>
                        <td data-label="Rate" className="num">
                          {inr(line.unit_rate)}
                        </td>
                        <td data-label="Amount" className="num">
                          {inr(line.amount)}
                        </td>
                        <td data-label="GST" className="num">
                          {num(line.gst_rate_pct, 0)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>

              <Panel title="What it adds up to">
                <dl className="kv">
                  <dt>Taxable value</dt>
                  <dd>{inr(invoice.taxable_value)}</dd>
                  {interState ? (
                    <>
                      <dt>IGST</dt>
                      <dd>{inr(invoice.igst)}</dd>
                    </>
                  ) : (
                    <>
                      <dt>CGST</dt>
                      <dd>{inr(invoice.cgst)}</dd>
                      <dt>SGST</dt>
                      <dd>{inr(invoice.sgst)}</dd>
                    </>
                  )}
                  {Number(invoice.rounding) !== 0 && (
                    <>
                      <dt>Rounding</dt>
                      <dd>{inr(invoice.rounding)}</dd>
                    </>
                  )}
                  <dt>
                    <strong>Total</strong>
                  </dt>
                  <dd>
                    <strong>{inr(invoice.total)}</strong>
                  </dd>
                  <dt>Paid</dt>
                  <dd>{inr(invoice.paid_amount)}</dd>
                  <dt>
                    <strong>Balance</strong>
                  </dt>
                  <dd>
                    <strong style={{ color: Number(invoice.balance) > 0 ? '#fbbf24' : '#4ade9f' }}>
                      {inr(invoice.balance)}
                    </strong>
                  </dd>
                </dl>
              </Panel>

              <Panel title={`Payments received (${invoice.payments.length})`} bodyless>
                {invoice.payments.length === 0 ? (
                  <Empty
                    title="Nothing received yet"
                    hint="Payments appear here once the transporter records them."
                  />
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Received</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th className="num">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td data-label="Received">{dateOnly(payment.paid_on)}</td>
                          <td data-label="Method">{payment.mode}</td>
                          <td data-label="Reference" className="mono">
                            {payment.reference ?? '—'}
                          </td>
                          <td data-label="Amount" className="num">
                            {inr(payment.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function InvoicesPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<string | null>(null)

  const invoicesQuery = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: () => api.get<Invoice[]>('/portal/invoices').then((r) => r.data),
  })

  const all = invoicesQuery.data ?? []

  const matches = (invoice: Invoice, which: Filter): boolean => {
    if (which === 'outstanding') return Number(invoice.balance) > 0
    if (which === 'settled') return Number(invoice.balance) <= 0
    return true
  }

  const shown = all.filter((invoice) => matches(invoice, filter))
  const owed = all.reduce((sum, invoice) => sum + Number(invoice.balance), 0)
  const overdue = all.filter(
    (invoice) =>
      Number(invoice.balance) > 0 &&
      invoice.due_date !== null &&
      new Date(invoice.due_date).getTime() < Date.now(),
  )

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-title">Invoices</div>
        <div className="page-sub">Every tax invoice raised to you, and what remains on it</div>
      </div>

      <div className="kpi-grid">
        <Kpi
          value={inr(owed)}
          label="Total outstanding"
          tone={owed > 0 ? 'warn' : 'good'}
          foot={`across ${all.filter((i) => Number(i.balance) > 0).length} invoice(s)`}
        />
        <Kpi
          value={overdue.length}
          label="Past due"
          tone={overdue.length > 0 ? 'alert' : 'good'}
          foot={overdue.length > 0 ? inr(overdue.reduce((s, i) => s + Number(i.balance), 0)) : 'nothing overdue'}
        />
        <Kpi value={all.length} label="Invoices raised" />
      </div>

      <div className="filters">
        {(['all', 'outstanding', 'settled'] as Filter[]).map((option) => (
          <button
            key={option}
            className={`filter-chip ${filter === option ? 'active' : ''}`}
            onClick={() => setFilter(option)}
          >
            {option === 'all' ? 'All' : option === 'outstanding' ? 'Outstanding' : 'Settled'} (
            {all.filter((i) => matches(i, option)).length})
          </button>
        ))}
      </div>

      <Panel title={`${shown.length} invoice(s)`} bodyless>
        {invoicesQuery.isLoading ? (
          <Loading />
        ) : invoicesQuery.error ? (
          <div style={{ padding: 16 }}>
            <ErrorBanner error={invoicesQuery.error} />
          </div>
        ) : shown.length === 0 ? (
          <Empty
            title="No invoices"
            hint={
              filter === 'all'
                ? 'Invoices appear here once the transporter raises them.'
                : 'Try another filter.'
            }
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((invoice) => (
                <tr key={invoice.id} className="clickable" onClick={() => setOpen(invoice.id)}>
                  <td data-label="Invoice" className="mono">
                    {invoice.invoice_number}
                  </td>
                  <td data-label="Date">{dateOnly(invoice.invoice_date)}</td>
                  <td data-label="Status">
                    <StatusChip status={invoice.status} />
                  </td>
                  <td data-label="Total" className="num">
                    {inr(invoice.total)}
                  </td>
                  <td data-label="Paid" className="num">
                    {inr(invoice.paid_amount)}
                  </td>
                  <td data-label="Balance" className="num">
                    {inr(invoice.balance)}
                  </td>
                  <td data-label="Due">
                    <DueChip date={invoice.due_date} balance={invoice.balance} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {open && <InvoiceDrawer invoiceId={open} onClose={() => setOpen(null)} />}
    </div>
  )
}
